import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Divider
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { assistantService, authService, callService, phoneProviderService } from '../utils/api';

function safeParseJson(value) {
  const v = (value ?? '').trim();
  if (!v) return {};
  try {
    return JSON.parse(v);
  } catch {
    return value;
  }
}

function CreateCall() {
  const { assistantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [assistant, setAssistant] = useState(null);
  const [phoneProviders, setPhoneProviders] = useState([]);

  const [phoneId, setPhoneId] = useState('');
  const [number, setNumber] = useState('');
  const [varsRows, setVarsRows] = useState([{ key: 'additionalProp1', value: '{}' }]);
  const [lastResponse, setLastResponse] = useState(null);

  const userRole = authService.getUserRole();
  const canEdit = userRole === 'super_admin' || userRole === 'admin' || userRole === 'moderator';

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        const [asst, phones] = await Promise.all([
          assistantService.getAssistantById(assistantId),
          phoneProviderService.listPhoneProviders()
        ]);
        if (!mounted) return;
        setAssistant(asst);
        setPhoneProviders(phones.phone_providers || []);

        // pick first phone provider by default
        const firstId = phones.phone_providers?.[0]?.phone_id;
        if (firstId) setPhoneId(String(firstId));
      } catch (e) {
        toast.error(e?.message || 'Failed to load create call data');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (assistantId) load();
    return () => {
      mounted = false;
    };
  }, [assistantId]);

  const title = useMemo(
    () => assistant?.assistant_name || assistant?.name || 'Assistant',
    [assistant]
  );

  const handleVarChange = (idx, patch) => {
    setVarsRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => setVarsRows((prev) => [...prev, { key: '', value: '{}' }]);
  const deleteRow = (idx) => setVarsRows((prev) => prev.filter((_, i) => i !== idx));

  const buildAssistantVariables = () => {
    const out = {};
    for (const row of varsRows) {
      const k = (row.key || '').trim();
      if (!k) continue;
      out[k] = safeParseJson(row.value);
    }
    return Object.keys(out).length ? out : undefined;
  };

  const handleSubmit = async () => {
    if (!phoneId) {
      toast.error('Please select a phone ID');
      return;
    }
    if (!number.trim()) {
      toast.error('Please enter a number');
      return;
    }
    try {
      setSaving(true);
      setLastResponse(null);
      const payload = {
        assistant_id: assistantId,
        phone_id: phoneId,
        number: number.trim(),
        source: 'tellyflow_team', // hardcoded: all dialouts from Tellyflow Engine frontend are tracked
        assistant_variables: buildAssistantVariables()
      };

      const res = await callService.initiateDialout(payload);
      setLastResponse(res);
      toast.success('Dialout initiated');
    } catch (e) {
      toast.error(e?.detail || e?.message || 'Failed to initiate dialout');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', p: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              Create Call
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              {title} • Assistant ID: {assistantId}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Card elevation={0}>
                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" disabled={!canEdit}>
                          <InputLabel>Phone ID</InputLabel>
                          <Select
                            value={phoneId}
                            label="Phone ID"
                            onChange={(e) => setPhoneId(e.target.value)}
                          >
                            {(phoneProviders || []).map((p) => (
                              <MenuItem key={p.phone_id} value={String(p.phone_id)}>
                                {p.provider_name} — {String(p.phone_id).slice(0, 8)}…
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Number"
                          value={number}
                          onChange={(e) => setNumber(e.target.value)}
                          disabled={!canEdit}
                          placeholder="+91XXXXXXXXXX"
                        />
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 3, mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Assistant Variables
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Add key/value pairs. Value can be JSON (e.g. {'{}'} / {'{"name":"abc"}'}) or plain text.
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {varsRows.map((row, idx) => (
                        <Grid container spacing={1.5} key={idx} alignItems="flex-start">
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Key"
                              value={row.key}
                              onChange={(e) => handleVarChange(idx, { key: e.target.value })}
                              disabled={!canEdit}
                            />
                          </Grid>
                          <Grid item xs={12} sm={7}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Value"
                              value={row.value}
                              onChange={(e) => handleVarChange(idx, { value: e.target.value })}
                              disabled={!canEdit}
                              multiline
                              minRows={1}
                              maxRows={6}
                            />
                          </Grid>
                          <Grid item xs={12} sm={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <IconButton
                              onClick={() => deleteRow(idx)}
                              disabled={!canEdit || varsRows.length === 1}
                              size="small"
                              sx={{ mt: { xs: 0, sm: 0.5 } }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Grid>
                        </Grid>
                      ))}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={addRow}
                        disabled={!canEdit}
                      >
                        Add variable
                      </Button>

                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!canEdit || saving}
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                      >
                        {saving ? 'Calling…' : 'Initiate Dialout'}
                      </Button>
                    </Box>

                    {!canEdit && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontStyle: 'italic', mt: 1, display: 'block' }}
                      >
                        You need admin access to initiate calls.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={5}>
                <Card elevation={0}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                      Last Response
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {lastResponse ? (
                      <Box sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(lastResponse, null, 2)}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No dialout initiated yet.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
    </DashboardLayout>
  );
}

export default CreateCall;

